import { rehydrate } from 'overmind';
import { Context } from '../index';
import Constants from '../../../app.constants';
import { AccessListItem, BamxPerms, Organization } from './state';
import { User } from '../user/state';

const clientCredentials = {
  client_id: Constants.CLIENT_ID,
  client_secret: Constants.CLIENT_SECRET,
};

export const setIsAdmin = (context: Context, theValue: boolean) => {
  context.state.auth.isAdmin = theValue;
};
export const setAccessList = (context: Context, theValue: AccessListItem[]) => {
  context.state.auth.accessList = theValue;
};
export const setReported = (context: Context, theValue: boolean) => {
  context.state.auth.reported = theValue;
};
export const setJwt = (context: Context, theValue: string | null) => {
  context.state.auth.jwt = theValue;
};

export const resolveAuthenticated = (
  context: Context,
  { data, credentials }: { data: any; credentials: any },
) => {
  // console.log( 'resolveAuthenticated data: ', data );
  // console.log( 'resolveAuthenticated credentials: ', credentials );
  const accessTokenData = data.access_token_payload;

  context.actions.auth.setJwt(data.access_token);
  context.actions.auth.setReported(true);

  context.actions.auth.createAccessList(accessTokenData.bamx_perms);
  context.actions.auth.createSession({ data, credentials });
};

export const login = (context: Context, credentials: any) => {
  const loginData = { ...credentials, ...clientCredentials };

  return context.effects.api
    .post('/api/v0/login/', loginData)
    .then((resp: any) => {
      const data = resp.data.data[0];
      context.actions.auth.resolveAuthenticated({ data, credentials });

      return resp;
    })
    .then(() => {
      return context.actions.auth.loadTermsConditions();
    })
    .catch((err: any) => {
      // actions.auth.logout();
      return Promise.reject(err);
    });
};

export const logout = (context: Context, refreshPage: any) => {
  context.actions.session.destroy();
  context.actions.auth.setJwt(null);

  return context.effects.api.post('/api/v0/logout/').then(() => {
    if (refreshPage) window.location.assign('/v2/login');
  });
};

export const createAccessList = (context: Context, bamxPerms: BamxPerms) => {
  const accessList: any[] = [];

  context.actions.auth.setIsAdmin(false);

  Object.keys(bamxPerms).forEach(orgKey => {
    const orgList = bamxPerms[orgKey];
    if (orgKey === 'admin') {
      context.actions.auth.setIsAdmin(true);

      accessList.push({
        type: 'admin',
        label: 'admin',
        orgId: 0,
        termsAccepted: true,
        isOrgAdmin: true,
      });
    } else if (orgKey === 'bamx') {
      // i.e. {bamx: {product_match: true}}
      Object.keys(orgList).forEach(accessType => {
        if (!orgList[accessType]) return;

        accessList.push({
          type: accessType,
          label: accessType,
          orgId: 0,
          termsAccepted: true,
          isOrgAdmin: true,
        });
      });
    } else {
      Object.keys(orgList).forEach(orgId => {
        accessList.push({
          type: orgKey,
          label: context.actions.auth.getUserRoleLabel(orgKey),
          orgId: parseInt(orgId),
          // termsAccepted gets updated by loadTermsConditions()
          termsAccepted: orgKey === 'merchants',
          isOrgAdmin: orgList[orgId].is_org_admin,
          isBillingUser: orgList[orgId].is_billing_user,
        });
      });
    }
  });

  context.actions.auth.setAccessList(accessList);
};

export const createSession = (
  context: Context,
  { data, credentials }: { data: any; credentials: any },
) => {
  const accessToken = data.access_token_payload;

  // console.log( 'createSession data.access_token_payload: ', JSON.stringify(data.access_token_payload) );
  // console.log( 'createSession accessToken: ', JSON.stringify(accessToken) );

  context.actions.session.create({
    authVersion: Constants.AUTH_VERSION,
    bamxVersion: accessToken.bamx_version || 0,
    orgId: 0,
    termsAccepted: false,
    isOrgAdmin: false,
    isAdmin: context.state.auth.isAdmin,
    userId: accessToken.sub,
    userName: credentials.username,
    userRole: null,
    email: accessToken.email,
    firstName: accessToken.given_name,
    lastName: accessToken.family_name,
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    exp: accessToken.exp,
  });

  return accessToken.sub;
};

export const updateSession = (
  context: Context,
  { theOrg, allSites = false }: { theOrg: Organization; allSites?: boolean },
): any => {
  let org: Organization = theOrg || {};

  const publisherType = org.publisher_type || null;

  if (org.pub_id) {
    org = {
      orgId: org.pub_id,
      type: 'publishers',
    };
  } else if (org.merch_id) {
    org = {
      orgId: org.merch_id,
      type: 'merchants',
    };
  } else {
    org = {
      orgId: org.orgId || 0,
      type: org.type,
    };
  }

  if (context.state.session.sessionData.isAdmin) {
    Object.assign(org, {
      type: org.type || 'admin',
      isOrgAdmin: true,
      termsAccepted: true,
    });
  } else {
    org = context.actions.auth.getOrgDataFromAccessList(org);
  }

  /*
   * New User flow handles terms and conditions redirect for primary and secondary users
   * Update session even if user is not an org admin or has not accepted terms.
   */
  try {
    context.actions.session.update({
      orgId: org.orgId,
      userRole: org.type,
      isOrgAdmin: org.isOrgAdmin,
      termsAccepted: org.termsAccepted,
      publisher_type: publisherType,
      allSites,
    });
    return context.state.session.sessionData;
  } catch (e) {
    console.log('problem? e: ', e);
    return Promise.reject(new Error('Could not set user to an account'));
  }
};

export const loadOrganizations = (context: Context) => {
  const promise = context.state.session.sessionData.isAdmin
    ? Promise.resolve({})
    : context.actions.auth.buildOrgRequests(context.state.session.sessionData);

  return Promise.all(promise).then(responses => {
    let organizations: Organization[] = [];
    responses.forEach((resp: any) => {
      organizations = organizations.concat(resp.data.data);
    });

    return context.actions.auth.buildOrgDicts(organizations);
  });
};

export const loadTermsConditions = (context: Context) => {
  let org: any = null;
  const loadData = context.state.session.sessionData.isAdmin
    ? Promise.resolve({})
    : context.actions.auth.loadOrganizations();

  return loadData.then((orgsByType: any) => {
    const { accessList } = context.state.auth;

    accessList.forEach((accessObject: any) => {
      let orgData;
      try {
        const orgs = orgsByType[accessObject.type];
        orgData = orgs[accessObject.orgId];
        if (orgData.hasOwnProperty('terms_accepted')) {
          accessObject.termsAccepted = orgData.terms_accepted;
        }
      } catch (e) {
        console.warn('error getting org type');
      }

      /*
       * New User flow handles terms and conditions redirect for primary and secondary users
       * Update session even if user is not an org admin or has not accepted terms.
       */
      if (accessObject.orgId === context.state.session.sessionData.orgId || !org) {
        org = accessObject;
        org.publisher_type = orgData ? orgData.publisher_type : null;
      }
    });

    rehydrate(context.actions.auth, { accessList });

    // actions.auth.setAccessList(accessList);

    return context.actions.auth.updateSession({ theOrg: org });
  });
};

export const changePassword = (
  context: Context,
  { credentials, reloadPage }: { credentials: any; reloadPage?: any },
) => {
  Object.assign(credentials, clientCredentials);

  return context.effects.api.post('/api/v0/accounts/password/change/', credentials).then(() => {
    if (reloadPage) window.location.reload();
  });
};

export const completeResetPassword = (
  context: Context,
  { resetToken, newPassword }: { resetToken: any; newPassword: any },
) => {
  const data = {
    reset_token: resetToken,
    new_password: newPassword,
    ...clientCredentials,
  };
  return context.effects.api.post('/api/v0/accounts/password/reset_completions/', data);
};

export const requestResetPassword = (context: Context, username: any) => {
  const data = { username, ...clientCredentials };
  return context.effects.api.post('/api/v0/accounts/password/reset_requests/', data);
};

export const buildOrgRequests = (context: Context, user: User) => {
  /*
   * Pass user object as an argument so this function
   * can be used by the OrgSelect component
   */
  let requests: any = [];
  let pubParams = {};
  let merchParams = {};

  if (user.isAdmin) {
    // Not passing org IDs returns all entities
    requests = [
      context.effects.api.get('/api/v0/merchants/'),
      context.effects.api.get('/api/v0/publishers/'),
    ];
  } else {
    const [merchantIds, publisherIds] = context.actions.auth.getOrgIds(user);

    if (merchantIds.length) {
      merchParams = { merchants: merchantIds.join(',') };
      requests.push(context.effects.api.get('/api/v0/merchants/', merchParams));
    }
    if (publisherIds.length) {
      pubParams = { publishers: publisherIds.join(',') };
      requests.push(context.effects.api.get('/api/v0/publishers/', pubParams));
    }
  }

  return requests;
};

export const buildOrgDicts = (context: Context, organizations: Organization[]) => {
  const orgDicts: any = { merchants: {}, publishers: {} };

  organizations.forEach((org: any) => {
    const orgType = org.pub_id ? 'publishers' : 'merchants';
    const orgId = org.pub_id || org.merch_id || 0;
    orgDicts[orgType][orgId] = org;
  });

  return orgDicts;
};

export const getOrgIds = (context: Context, user: User): any => {
  // Get the IDs of organizations the user has access to from accessList
  const merchantIds: any[] = [];
  const publisherIds: any[] = [];

  /*
   * accessList: [{
   *  type: "publishers", label: "publisher", orgId: 1054,
   *  termsAccepted: true, isOrgAdmin: false,
   * },]
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  user.accessList?.forEach((org: any) => {
    if (org.type === 'merchants') {
      merchantIds.push(org.orgId);
    } else if (org.type === 'publishers') {
      publisherIds.push(org.orgId);
    }
  });

  return [merchantIds, publisherIds];
};

export const getOrgDataFromAccessList = (context: Context, org: Organization) => {
  return (
    context.state.auth.accessList.find((accessObject: any) => {
      return org.orgId === accessObject.orgId && org.type === accessObject.type;
    }) || {}
  );
};

export const getUserRoleLabel = (context: Context, role: string) => {
  switch (role) {
    case 'merchants':
      return 'retailer';
    case 'publishers':
      return 'publisher';
    default:
      return role;
  }
};
